import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { Plus, Trash2, Save, X, Table2, ChevronDown, ChevronUp, Edit2, Zap, Fuel, Plane, Car, Factory } from "lucide-react";

export default function AdminDataTables() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // The table being edited
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Emission Factors state
  const [activeTab, setActiveTab] = useState('tables'); // 'tables' or 'emission-factors'
  const [emissionFactors, setEmissionFactors] = useState(null);
  const [emissionSource, setEmissionSource] = useState('');
  const [emissionLoading, setEmissionLoading] = useState(false);
  const [emissionSaving, setEmissionSaving] = useState(false);

  // Form state for create/edit
  const [form, setForm] = useState({
    name: "",
    description: "",
    table_type: "ghg",
    columns: [],
    rows: []
  });

  const load = () => {
    setLoading(true);
    api.getDataTables()
      .then(data => setTables(data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const loadEmissionFactors = async () => {
    setEmissionLoading(true);
    try {
      const data = await api.getEmissionFactors();
      setEmissionFactors(data.factors || {});
      setEmissionSource(data.source || 'UK DEFRA / IEA / EPA');
    } catch (err) {
      console.error('Failed to load emission factors:', err);
    }
    setEmissionLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (activeTab === 'emission-factors' && !emissionFactors) {
      loadEmissionFactors();
    }
  }, [activeTab]);

  const resetForm = () => {
    setForm({ name: "", description: "", table_type: "ghg", columns: [], rows: [] });
  };

  const startCreate = () => {
    resetForm();
    setEditing(null);
    setShowCreate(true);
  };

  const startEdit = (table) => {
    setForm({
      name: table.name || "",
      description: table.description || "",
      table_type: table.table_type || "ghg",
      columns: table.columns || [],
      rows: table.rows || []
    });
    setEditing(table);
    setShowCreate(false);
  };

  const cancelEdit = () => {
    setEditing(null);
    setShowCreate(false);
    resetForm();
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert("Please enter a table name");
      return;
    }
    setSaving(true);
    try {
      if (showCreate) {
        await api.createDataTable(form);
      } else if (editing) {
        await api.updateDataTable(editing.id, form);
      }
      cancelEdit();
      load();
    } catch (err) {
      alert(err.message || "Failed to save");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this data table?")) return;
    try {
      await api.deleteDataTable(id);
      if (editing?.id === id) cancelEdit();
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  // Column management
  const addColumn = () => {
    const colName = prompt("Enter column name:");
    if (!colName?.trim()) return;
    const colType = prompt("Enter column type (text/number/decimal):", "text") || "text";
    const colUnit = prompt("Enter unit (optional):", "") || "";
    setForm(f => ({
      ...f,
      columns: [...f.columns, { name: colName.trim(), type: colType, unit: colUnit }]
    }));
  };

  const removeColumn = (idx) => {
    const col = form.columns[idx];
    setForm(f => ({
      ...f,
      columns: f.columns.filter((_, i) => i !== idx),
      rows: f.rows.map(r => {
        const nr = { ...r };
        delete nr[col.name];
        return nr;
      })
    }));
  };

  // Row management
  const addRow = () => {
    const emptyRow = {};
    form.columns.forEach(c => { emptyRow[c.name] = ""; });
    setForm(f => ({ ...f, rows: [...f.rows, emptyRow] }));
  };

  const updateCell = (rowIdx, colName, value) => {
    setForm(f => {
      const rows = [...f.rows];
      rows[rowIdx] = { ...rows[rowIdx], [colName]: value };
      return { ...f, rows };
    });
  };

  const removeRow = (idx) => {
    setForm(f => ({ ...f, rows: f.rows.filter((_, i) => i !== idx) }));
  };

  const inputCls = "bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500";

  const isEditing = showCreate || editing;

  // Emission factor helpers
  const updateEmissionFactor = (category, key, value) => {
    setEmissionFactors(prev => {
      if (category === 'electricity') {
        return {
          ...prev,
          electricity: { ...prev.electricity, [key]: parseFloat(value) || 0 }
        };
      }
      return { ...prev, [key]: parseFloat(value) || 0 };
    });
  };

  const saveEmissionFactors = async () => {
    setEmissionSaving(true);
    try {
      await api.updateEmissionFactors({
        factors: emissionFactors,
        source: emissionSource
      });
      alert('Emission factors updated successfully!');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
    setEmissionSaving(false);
  };

  return (
    <div data-testid="admin-data-tables-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Data Tables & Emission Factors</h1>
        {activeTab === 'tables' && !isEditing && (
          <button onClick={startCreate} data-testid="create-table-btn"
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" /> New Table
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('tables')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'tables'
              ? 'bg-emerald-600 text-white'
              : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
          }`}
        >
          <Table2 className="w-4 h-4" /> Data Tables
        </button>
        <button
          onClick={() => setActiveTab('emission-factors')}
          data-testid="emission-factors-tab"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'emission-factors'
              ? 'bg-emerald-600 text-white'
              : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
          }`}
        >
          <Fuel className="w-4 h-4" /> Emission Factors
        </button>
      </div>

      {/* Emission Factors Tab */}
      {activeTab === 'emission-factors' && (
        <div className="space-y-6">
          {emissionLoading ? (
            <div className="text-center py-12 text-gray-400">Loading emission factors...</div>
          ) : emissionFactors ? (
            <>
              {/* Source Info */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Data Source</label>
                <input
                  value={emissionSource}
                  onChange={e => setEmissionSource(e.target.value)}
                  placeholder="e.g., UK DEFRA / IEA / EPA"
                  className={`w-full ${inputCls}`}
                  data-testid="emission-source-input"
                />
                <p className="text-xs text-gray-400 mt-1">Cite the source of your emission factors for compliance</p>
              </div>

              {/* Electricity by Country */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" /> Electricity Emission Factors (kg CO2e/kWh)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(emissionFactors.electricity || {}).map(([country, value]) => (
                    <div key={country}>
                      <label className="block text-xs text-gray-500 mb-1 capitalize">{country.replace(/_/g, ' ')}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={value}
                        onChange={e => updateEmissionFactor('electricity', country, e.target.value)}
                        className={`w-full ${inputCls}`}
                        data-testid={`ef-electricity-${country}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Fuels */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Fuel className="w-4 h-4 text-orange-500" /> Fuel Emission Factors (kg CO2e/liter or kWh)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['diesel', 'petrol', 'heating_oil', 'natural_gas'].map(fuel => (
                    <div key={fuel}>
                      <label className="block text-xs text-gray-500 mb-1 capitalize">{fuel.replace(/_/g, ' ')} {fuel === 'natural_gas' ? '(kWh)' : '(liter)'}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={emissionFactors[fuel] || 0}
                        onChange={e => updateEmissionFactor(null, fuel, e.target.value)}
                        className={`w-full ${inputCls}`}
                        data-testid={`ef-${fuel}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Travel */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Plane className="w-4 h-4 text-blue-500" /> Travel Emission Factors (kg CO2e/km)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['flight_short', 'flight_long', 'flight_average', 'car_travel', 'train', 'bus'].map(mode => (
                    <div key={mode}>
                      <label className="block text-xs text-gray-500 mb-1 capitalize">{mode.replace(/_/g, ' ')}</label>
                      <input
                        type="number"
                        step="0.001"
                        value={emissionFactors[mode] || 0}
                        onChange={e => updateEmissionFactor(null, mode, e.target.value)}
                        className={`w-full ${inputCls}`}
                        data-testid={`ef-${mode}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Waste */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Factory className="w-4 h-4 text-gray-500" /> Waste Emission Factors (kg CO2e/tonne)
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {['waste_landfill', 'waste_recycled', 'waste_composted'].map(waste => (
                    <div key={waste}>
                      <label className="block text-xs text-gray-500 mb-1 capitalize">{waste.replace(/_/g, ' ')}</label>
                      <input
                        type="number"
                        step="1"
                        value={emissionFactors[waste] || 0}
                        onChange={e => updateEmissionFactor(null, waste, e.target.value)}
                        className={`w-full ${inputCls}`}
                        data-testid={`ef-${waste}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={saveEmissionFactors}
                  disabled={emissionSaving}
                  data-testid="save-emission-factors-btn"
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  <Save className="w-4 h-4" /> {emissionSaving ? 'Saving...' : 'Save Emission Factors'}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-400">Failed to load emission factors</div>
          )}
        </div>
      )}

      {/* Data Tables Tab */}
      {activeTab === 'tables' && (
        <>
          {/* Editor Panel */}
      {isEditing && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">{showCreate ? 'Create New Table' : `Edit: ${editing?.name}`}</h2>
            <div className="flex gap-2">
              <button onClick={cancelEdit} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} data-testid="save-table-btn"
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Table'}
              </button>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Table Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., GHG Emission Factors" data-testid="table-name-input"
                className={`w-full ${inputCls}`} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Type</label>
              <select value={form.table_type} onChange={e => setForm(f => ({ ...f, table_type: e.target.value }))}
                className={`w-full ${inputCls}`}>
                <option value="ghg">GHG Emissions</option>
                <option value="conversion">Conversion Factors</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Description</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description" className={`w-full ${inputCls}`} />
            </div>
          </div>

          {/* Columns */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Columns ({form.columns.length})</label>
              <button onClick={addColumn} data-testid="add-column-btn"
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                <Plus className="w-4 h-4" /> Add Column
              </button>
            </div>
            {form.columns.length === 0 ? (
              <p className="text-sm text-gray-400 py-3 text-center bg-gray-50 rounded-lg">No columns defined. Click "Add Column" to start.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {form.columns.map((col, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                    <span className="font-medium text-gray-800">{col.name}</span>
                    {col.unit && <span className="text-gray-400 text-xs">({col.unit})</span>}
                    <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{col.type}</span>
                    <button onClick={() => removeColumn(idx)} className="text-red-400 hover:text-red-600 ml-1">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Data Rows */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Data Rows ({form.rows.length})</label>
              <button onClick={addRow} disabled={form.columns.length === 0} data-testid="add-row-btn"
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 disabled:text-gray-400">
                <Plus className="w-4 h-4" /> Add Row
              </button>
            </div>
            {form.columns.length === 0 ? (
              <p className="text-sm text-gray-400 py-3 text-center bg-gray-50 rounded-lg">Add columns first before adding data rows.</p>
            ) : form.rows.length === 0 ? (
              <p className="text-sm text-gray-400 py-3 text-center bg-gray-50 rounded-lg">No data rows. Click "Add Row" to add data.</p>
            ) : (
              <div className="overflow-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-10">#</th>
                      {form.columns.map(col => (
                        <th key={col.name} className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          {col.name} {col.unit && <span className="text-gray-400">({col.unit})</span>}
                        </th>
                      ))}
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {form.rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-gray-100 last:border-0">
                        <td className="px-3 py-2 text-gray-400 text-xs">{ri + 1}</td>
                        {form.columns.map(col => (
                          <td key={col.name} className="px-2 py-1.5">
                            <input value={row[col.name] ?? ""} onChange={e => updateCell(ri, col.name, e.target.value)}
                              type={col.type === 'number' || col.type === 'decimal' ? 'number' : 'text'}
                              className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-900 outline-none focus:ring-1 focus:ring-emerald-500" />
                          </td>
                        ))}
                        <td className="px-2 py-1.5">
                          <button onClick={() => removeRow(ri)} className="text-red-400 hover:text-red-600 p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading tables...</div>
      ) : tables.length === 0 && !isEditing ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Table2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-lg font-medium">No data tables yet</p>
          <p className="text-gray-400 text-sm mt-1">Create your first GHG or custom data table</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {tables.map(table => (
            <div key={table.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors"
              data-testid={`table-card-${table.id}`}>
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <Table2 className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-gray-900 font-medium">{table.name}</p>
                    <p className="text-xs text-gray-400">
                      {table.table_type} | {(table.columns || []).length} columns | {(table.rows || []).length} rows
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(table)} data-testid={`edit-table-${table.id}`}
                    className="p-2 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-gray-50 transition-colors">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(table.id)} data-testid={`delete-table-${table.id}`}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-50 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Preview of columns */}
              {(table.columns || []).length > 0 && (
                <div className="border-t border-gray-100 px-5 py-3 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-2">Columns:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(table.columns || []).map((col, i) => (
                      <span key={i} className="text-xs bg-white border border-gray-200 rounded px-2 py-1 text-gray-700">
                        {col.name} {col.unit && <span className="text-gray-400">({col.unit})</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
